/**
 * Work-queue identity vs optional claim/denial link.
 * Manual / shell-created tasks use synthetic ids (manual-*, wq-*). Those must
 * never be treated as Core Claims / Denial claim ids. Users may optionally
 * attach a real claim or denial number.
 *
 * The Task Manager queue item UUID (item.id) is also never a claim/denial id.
 */

const SYNTHETIC_PREFIXES = [
  'manual-',
  'wq-',
  'denial_mgmt-manual-',
  'ar_posting-manual-',
  'ins_ver-manual-',
  'prior_auth-manual-',
  'claims-manual-',
  'charge-manual-',
  'remittance-manual-',
  'patient_billing-manual-',
  'contract-manual-',
  'compliance-manual-',
];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isSyntheticWorkQueueId(value) {
  const id = String(value || '').trim().toLowerCase();
  if (!id) return false;
  return SYNTHETIC_PREFIXES.some((p) => id.startsWith(p));
}

export function isUuidString(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

/**
 * True when `value` is the work-queue row's own identity (TM id / domain id),
 * not a user-linked claim/denial number.
 */
export function isWorkQueueOwnId(value, item) {
  const id = String(value || '').trim();
  if (!id || !item || typeof item !== 'object') return false;
  const ownIds = [
    item.id,
    item.queue_item_id,
    item.external_id,
    item.domain_id,
    item.exception_id,
    item.metadata?.domain_id,
    item.item_metadata?.domain_id,
  ]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
  return ownIds.includes(id);
}

export function normalizeLinkedClaimId(raw, item) {
  const value = String(raw || '').trim();
  if (!value || isSyntheticWorkQueueId(value)) return '';
  if (item && isWorkQueueOwnId(value, item)) return '';
  return value;
}

export function extractLinkedClaimId(item) {
  if (!item || typeof item !== 'object') return '';
  const meta =
    (item.item_metadata && typeof item.item_metadata === 'object' && item.item_metadata) ||
    (item.metadata && typeof item.metadata === 'object' && item.metadata) ||
    {};
  const data = item.item_data && typeof item.item_data === 'object' ? item.item_data : {};
  const candidates = [
    meta.linked_claim_id,
    meta.linked_denial_id,
    data.linked_claim_id,
    data.linked_denial_id,
    item.linked_claim_id,
    item.linked_denial_id,
    item.linkedClaimId,
  ];
  for (const c of candidates) {
    const n = normalizeLinkedClaimId(c, item);
    if (n) return n;
  }
  // claim_id is only a link when it is not the queue row's own id / synthetic id
  const maybeClaim = data.claim_id || item.claim_id || item.claimId;
  return normalizeLinkedClaimId(maybeClaim, item);
}

function workQueueValueBags(item) {
  if (!item || typeof item !== 'object') return [];
  return [item, item.item_data, item.item_metadata, item.metadata].filter(
    (bag) => bag && typeof bag === 'object'
  );
}

/**
 * Safe entity id for "Open claim/contract/…" actions from a work-queue row.
 * Never returns the TM queue item UUID.
 *
 * @param {object} item
 * @param {string[]} [extraKeys]
 * @param {{ allowDomainId?: boolean, skipLinkedClaim?: boolean }} [options]
 *   allowDomainId — keep posting_id / eob_id even when they equal TM external_id
 *   skipLinkedClaim — do not prefer linked_claim_id (AR posting opens the payment, not a claim)
 */
export function resolveOpenableLinkedId(item, extraKeys = [], options = {}) {
  const allowDomainId = Boolean(options.allowDomainId);
  const skipLinkedClaim = Boolean(options.skipLinkedClaim);
  const rejectOwnTmId = (value) => {
    const id = String(value || '').trim();
    if (!id || isSyntheticWorkQueueId(id)) return '';
    if (item && (String(item.id || '').trim() === id || String(item.queue_item_id || '').trim() === id)) {
      return '';
    }
    if (!allowDomainId && item && isWorkQueueOwnId(id, item)) return '';
    return id;
  };

  if (!skipLinkedClaim) {
    const linked = extractLinkedClaimId(item);
    if (linked) return linked;
  }
  for (const key of extraKeys) {
    for (const bag of workQueueValueBags(item)) {
      const n = rejectOwnTmId(bag[key]);
      if (n) return n;
    }
  }
  return '';
}

export function canDeleteWorkQueueTask(role) {
  const r = String(role || '').trim().toLowerCase();
  return r === 'owner' || r === 'admin';
}

export async function fetchCurrentOrgRole(saasApiBaseUrl) {
  const cached =
    (typeof localStorage !== 'undefined' && localStorage.getItem('current_user_role')) || '';
  const token =
    (typeof localStorage !== 'undefined' &&
      (localStorage.getItem('token') || localStorage.getItem('auth_token'))) ||
    '';
  const base = String(saasApiBaseUrl || '').replace(/\/$/, '');
  if (!token || !base) return cached;
  try {
    const response = await fetch(`${base}/organizations/current`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });
    if (!response.ok) return cached;
    const data = await response.json();
    const role = String(data.current_user_role || data.role || '').trim();
    if (role && typeof localStorage !== 'undefined') {
      localStorage.setItem('current_user_role', role);
    }
    return role || cached;
  } catch {
    return cached;
  }
}
